// src/pages/PublicDashboard.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api, createSocialComment, fetchFixtureEdges, fetchFixtureSocialPostTags, fetchMyTeamRooms, fetchSocialComments } from "../api";
import FeaturedRecord from "../components/FeaturedRecord";
import { useAuth } from "../components/AuthGate";
import { placeAndTrackEdge } from "../utils/placeAndTrack";
import PremiumUpsellBanner from "../components/PremiumUpsellBanner";
import { getBookmakerUrl } from "../utils/bookmakers";
import { prettyCompetitionName } from "../utils/competitionNames";
import { formatMarketName } from "../utils/formatMarketName";
import { isAllowedPublicEdge, normalizeEdgePercent } from "../utils/publicEdge";
import FixtureAccessPill from "../components/FixtureAccessPill";
import SiteFooterDisclaimer from "../components/SiteFooterDisclaimer";
import { usePreferences } from "../context/PreferencesContext";
import PublicChat from "../components/PublicChat";
import SportsNewsSection from "../components/SportsNewsSection";
import { FixtureSocialPreview } from "../components/FixtureSocialPosts";
import { getClubRoomCopy } from "../components/ClubRoomSection";
import dashboardStyles from "../styles/PublicDashboard.module.css";
import { slugifyTeamName } from "../utils/slugify";

/* ---------------- utils ---------------- */
const FINISHED_STATUSES = ["FT", "AET", "PEN", "FINISHED", "FULLTIME"];
const LIVE_STATUSES = ["1H", "2H", "HT", "ET", "LIVE", "INPLAY", "BREAK", "INT"];
const APP_STORE_URL = "https://apps.apple.com/gb/app/csb-live-sports-analysis/id6759716509";

const getFixtureStatusMeta = (fixture = {}) => {
  const fixtureObj = typeof fixture?.fixture === "object" && fixture?.fixture ? fixture.fixture : null;
  const statusObj = typeof fixture?.status === "object" && fixture?.status ? fixture.status : null;
  const nestedStatusObj = typeof fixtureObj?.status === "object" && fixtureObj?.status ? fixtureObj.status : null;
  const raw = String(
    fixture?.status_short ||
    fixtureObj?.status_short ||
    statusObj?.short ||
    nestedStatusObj?.short ||
    statusObj?.code ||
    nestedStatusObj?.code ||
    fixture?.match_status ||
    fixture?.fixture_status ||
    fixture?.status ||
    ""
  ).toUpperCase();
  const elapsed = Number(
    fixture?.elapsed ??
    fixture?.status_elapsed ??
    fixture?.live_minute ??
    fixture?.minute ??
    statusObj?.elapsed ??
    nestedStatusObj?.elapsed
  );
  const isFinished = FINISHED_STATUSES.includes(raw);
  const isLive = LIVE_STATUSES.includes(raw) || (!isFinished && Number.isFinite(elapsed) && elapsed > 0);
  return { raw, elapsed, isFinished, isLive };
};

const getFixtureScoreMeta = (fixture = {}) => {
  const fixtureObj = typeof fixture?.fixture === "object" && fixture?.fixture ? fixture.fixture : null;
  const home =
    fixture?.home_score ??
    fixture?.goals_home ??
    fixture?.score_home ??
    fixture?.score?.home ??
    fixture?.score?.fulltime?.home ??
    fixture?.score?.full_time?.home ??
    fixture?.scores?.home ??
    fixture?.scores?.fulltime?.home ??
    fixtureObj?.goals?.home ??
    fixtureObj?.score?.home ??
    fixtureObj?.score?.fulltime?.home ??
    fixture?.goals?.home;
  const away =
    fixture?.away_score ??
    fixture?.goals_away ??
    fixture?.score_away ??
    fixture?.score?.away ??
    fixture?.score?.fulltime?.away ??
    fixture?.score?.full_time?.away ??
    fixture?.scores?.away ??
    fixture?.scores?.fulltime?.away ??
    fixtureObj?.goals?.away ??
    fixtureObj?.score?.away ??
    fixtureObj?.score?.fulltime?.away ??
    fixture?.goals?.away;
  const hasScore = home !== null && home !== undefined && away !== null && away !== undefined;
  return { home, away, hasScore };
};

const getFixtureTimeLabel = (fixture) => {
  const status = getFixtureStatusMeta(fixture);
  const score = getFixtureScoreMeta(fixture);
  if (status.isFinished && score.hasScore) return `${score.home}-${score.away} FT`;
  if (status.isFinished) return "FT";
  if (status.raw === "HT" && score.hasScore) return `${score.home}-${score.away} HT`;
  if (status.raw === "HT") return "HT";
  if (status.isLive && score.hasScore) return `${score.home}-${score.away} ${Number.isFinite(status.elapsed) ? `${status.elapsed}'` : "LIVE"}`;
  if (status.isLive) return Number.isFinite(status.elapsed) ? `${status.elapsed}'` : "LIVE";
  return toUK(fixture?.kickoff_utc);
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const toUK = (iso, { withZone = false } = {}) => {
  if (!iso) return "—";
  const fmt = { timeZone: "Europe/London", hour: "2-digit", minute: "2-digit" };
  if (withZone) fmt.timeZoneName = "short";
  try {
    const d = new Date(iso);
    if (!isNaN(d)) return d.toLocaleTimeString("en-GB", fmt);
  } catch {}
  try {
    const safe = iso.endsWith("Z") || iso.includes("+") ? iso : iso + "Z";
    return new Date(safe).toLocaleTimeString("en-GB", fmt);
  } catch {
    return iso.slice(11, 16);
  }
};

const featuredKickoffLabel = (iso, anchorDay) => {
  if (!iso) return "—";
  const kickoff = new Date(iso);
  if (Number.isNaN(kickoff.getTime())) return toUK(iso, { withZone: true });
  const kickoffDay = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(kickoff);
  const time = toUK(iso, { withZone: true });
  if (!anchorDay || kickoffDay === anchorDay) return time;
  const dateLabel = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(kickoff);
  return `${dateLabel} · ${time}`;
};

const slug = (s = "") =>
  s
    .normalize("NFKD")
    .replace(/[^\w\s.-]/g, "")
    .trim()
    .replace(/\s+/g, "_")
    .toLowerCase();

// Premium rugby leagues to display (matching Rugby.jsx)
const PREMIUM_RUGBY_LEAGUES = [
  "SIX NATIONS",
  "PREMIERSHIP",
  "UNITED RUGBY CHAMPIONSHIP",
  "URC",
  "EUROPEAN RUGBY CHAMPIONS CUP",
  "ERCC",
  "TOP 14",
  "RUGBY WORLD CUP",
];

const prettyComp = (code) => prettyCompetitionName(code, "Football");
const logoUrl = (team) => `/logos/${slug(team)}.png`;
const playerHeadshot = (playerId) =>
  playerId ? `https://img.mlbstatic.com/mlb-photos/image/upload/w_180,q_auto:best/v1/people/${playerId}/headshot/67/current` : "";

const normalizeBaseballPropMarket = (value = "") => {
  const key = String(value || "").trim().toLowerCase();
  if (key.startsWith("player_") || key.startsWith("pitcher_")) return key;
  return "";
};

const parseBaseballPropKey = (market = "") => {
  const key = normalizeBaseballPropMarket(market);
  if (!key) return { group: "", side: "" };
  if (key.endsWith("_over")) return { group: key.slice(0, -5), side: "over" };
  if (key.endsWith("_under")) return { group: key.slice(0, -6), side: "under" };
  return { group: key, side: "" };
};

const formatBaseballPropMarket = (market = "") => {
  const { group } = parseBaseballPropKey(market);
  const map = {
    player_home_runs: "Home Runs",
    player_hits: "Hits",
    player_rbi: "RBI",
    player_runs: "Runs",
    player_total_bases: "Total Bases",
    player_singles: "Singles",
    player_doubles: "Doubles",
    player_triples: "Triples",
    player_stolen_bases: "Stolen Bases",
    pitcher_strikeouts: "Pitcher Strikeouts",
    pitcher_outs: "Pitcher Outs",
    pitcher_walks_issued: "Pitcher Walks Issued",
    pitcher_hits_allowed: "Pitcher Hits Allowed",
  };
  return map[group] || formatMarketName(market);
};

const formatBaseballPropSide = (market = "", line = null) => {
  const { side } = parseBaseballPropKey(market);
  const label = side ? `${side.charAt(0).toUpperCase()}${side.slice(1)}` : "Line";
  return line != null ? `${label} ${line}` : label;
};

const footballPropGroup = (market = "", marketLabel = "") => {
  const key = String(market || "").trim().toLowerCase();
  const label = String(marketLabel || "").trim().toLowerCase();
  const haystack = [key, label].filter(Boolean).join(" ");
  if (!haystack) return "";
  if (
    ["yellow", "booked", "to_be_booked"].includes(key) ||
    haystack.includes("booked") ||
    haystack.includes("yellow") ||
    haystack.includes("to be booked")
  ) {
    return "yellow";
  }
  if (
    ["sot", "shots_on_target"].includes(key) ||
    haystack.includes("shots on target") ||
    haystack.includes("shot on target") ||
    haystack.includes("sot")
  ) {
    return "sot";
  }
  if (haystack.includes("shots")) return "shots";
  if (haystack.includes("fouls")) return "fouls";
  if (haystack.includes("tackles")) return "tackles";
  if (haystack.includes("assists")) return "assists";
  return "";
};

const isFootballPropMarket = (market = "", marketLabel = "") => !!footballPropGroup(market, marketLabel);

const formatFootballPropMarket = (market = "", marketLabel = "") => {
  const group = footballPropGroup(market, marketLabel);
  const map = {
    yellow: "To Be Booked",
    sot: "Shots on Target",
    shots: "Shots",
    fouls: "Fouls",
    tackles: "Tackles",
    assists: "Assists",
  };
  return map[group] || marketLabel || formatMarketName(market);
};

const formatFootballPropSide = (market = "", line = null, sideLabel = "", marketLabel = "") => {
  if (sideLabel) return sideLabel;
  const key = String(market || "").trim().toLowerCase();
  const group = footballPropGroup(market, marketLabel);
  if (group === "yellow") {
    return line != null ? `Over ${line}` : "Yes";
  }
  if (key.endsWith("_over")) return line != null ? `Over ${line}` : "Over";
  if (key.endsWith("_under")) return line != null ? `Under ${line}` : "Under";
  return line != null ? `Over ${line}` : "";
};

const parsePlayerPropNote = (note = "") => {
  const text = String(note || "").trim();
  const match = text.match(/^MLB Prop:\s*(.+?)\s+—\s+(.+?)\s+—\s+(.+?)(?:\.|$)/i);
  if (!match) return null;
  return {
    playerName: match[1]?.trim() || "",
    marketLabel: match[2]?.trim() || "",
    sideLabel: match[3]?.trim() || "",
  };
};

const getFeaturedPickDescriptor = (pick = {}) => {
  const noteProp = parsePlayerPropNote(pick.note);
  const rawPlayerProp = !!normalizeBaseballPropMarket(pick.market);
  const rawFootballProp = isFootballPropMarket(pick.market, pick.market_label);
  const isPlayerProp =
    String(pick.pick_type || "").toLowerCase() === "player_prop" ||
    rawPlayerProp ||
    rawFootballProp ||
    !!noteProp ||
    !!pick.player_name ||
    !!pick.market_label ||
    !!pick.side_label;
  const playerName = pick.player_name || noteProp?.playerName || "";
  const marketLabel =
    pick.market_label ||
    noteProp?.marketLabel ||
    (rawFootballProp
      ? formatFootballPropMarket(pick.market, pick.market_label)
      : rawPlayerProp
      ? formatBaseballPropMarket(pick.market)
      : "");
  const sideLabel =
    pick.side_label ||
    noteProp?.sideLabel ||
    (rawFootballProp
      ? formatFootballPropSide(pick.market, pick.line, pick.side_label, pick.market_label)
      : rawPlayerProp
      ? formatBaseballPropSide(pick.market, pick.line)
      : "");
  const label = isPlayerProp
    ? [playerName, marketLabel, sideLabel].filter(Boolean).join(" — ") || formatMarketName(pick.market)
    : formatMarketName(pick.market);
  return { isPlayerProp, playerName, label };
};

const featuredPickLabel = (pick = {}) => {
  return getFeaturedPickDescriptor(pick).label;
};

const featuredPickMeta = (pick = {}) => {
  const edge = normalizeEdgePercent(pick.edge);
  const descriptor = getFeaturedPickDescriptor(pick);
  return {
    isPlayerProp: descriptor.isPlayerProp,
    playerName: descriptor.playerName,
    edgeText: edge != null ? `${edge > 0 ? "+" : ""}${edge.toFixed(1)}%` : null,
    modelText: pick.model_prob != null ? `${(Number(pick.model_prob) * 100).toFixed(1)}%` : null,
    fairText: pick.fair_price != null ? Number(pick.fair_price).toFixed(2) : null,
  };
};

const routeFor = (f) => {
  const s = (f.sport || "").toLowerCase();
  const c = (f.comp || "").toLowerCase();
  if (s === "rugby" || s === "rugby union") return `/rugby/match/${f.id}`;
  if (s === "baseball" || s === "mlb" || c.includes("mlb") || c.includes("baseball")) {
    return `/baseball/game/${f.id}`;
  }
  if (
    s === "basketball" ||
    s === "nba" ||
    c === "nba" ||
    c.includes("nba") ||
    c.includes("basketball")
  ) {
    return `/basketball/game/${f.id}`;
  }
  if (
    s === "hockey" ||
    s === "ice_hockey" ||
    s === "ice hockey" ||
    s === "nhl" ||
    c === "nhl" ||
    c.includes("nhl") ||
    c.includes("hockey")
  ) {
    return `/nhl/game/${f.id}`;
  }
  if (s === "nfl" || s === "gridiron" || s === "american_football" || c === "nfl" || c.includes("nfl")) {
    return `/nfl/game/${f.id}`;
  }
  if (s === "cfb" || c.includes("college") || c === "ncaa") return `/cfb/fixture/${f.id}`;
  return `/fixture/${f.id}`;
};

const normBook = (x) =>
  String(x || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const isWilliamHill = (bm) => normBook(bm) === "williamhill";

const pickBestAllowedPublicEdge = (rows = []) => {
  if (!Array.isArray(rows) || !rows.length) return null;

  const filtered = rows
    .filter((row) => !isWilliamHill(row?.bookmaker))
    .filter((row) => isAllowedPublicEdge(row?.edge))
    .map((row) => {
      const normalizedEdgePercent = normalizeEdgePercent(row?.edge);
      return {
        market: row?.market || null,
        bookmaker: row?.bookmaker || null,
        edge: normalizedEdgePercent == null ? null : normalizedEdgePercent / 100,
        prob: typeof Number(row?.prob) === "number" && Number.isFinite(Number(row?.prob)) ? Number(row.prob) : null,
        price: typeof Number(row?.price) === "number" && Number.isFinite(Number(row?.price)) ? Number(row.price) : null,
        isTeaser: false,
        hasFullAccess: true,
      };
    })
    .filter((row) => row.edge != null)
    .sort((a, b) => b.edge - a.edge);

  return filtered[0] || null;
};

/* ----------- responsive hook ---------- */
const useIsMobile = (bp = 640) => {
  const [m, setM] = useState(
    () => (typeof window !== "undefined" ? window.innerWidth < bp : true)
  );
  useEffect(() => {
    const fn = () => setM(window.innerWidth < bp);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, [bp]);
  return m;
};

/* ---------------- styles -------------- */
const S = {
  page: { maxWidth: 1240, margin: "0 auto", padding: 18 },
  heroWrap: {
    background: "linear-gradient(165deg, rgba(15,25,19,0.95), rgba(10,18,13,0.95))",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 14,
    padding: 24,
    marginBottom: 16,
    boxShadow: "0 10px 26px rgba(0,0,0,0.2)",
  },
  heroTitle: { fontSize: "clamp(1.9rem, 4.2vw, 2.35rem)", lineHeight: 1.14, letterSpacing: "-0.01em", fontWeight: 820, marginBottom: 10, color: "#f3fff8", maxWidth: 770 },
  heroSub: { fontSize: 15, lineHeight: 1.58, color: "rgba(235,245,239,.88)", maxWidth: 740, marginBottom: 14 },
  heroBullets: { display: "grid", gap: 7, marginTop: 12, color: "#d9f7e8", fontSize: 14 },
  socialStrip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,.16)",
    background: "rgba(255,255,255,.04)",
    color: "#e4fff0",
    padding: "6px 12px",
    fontSize: 12,
    marginBottom: 12,
  },
  primaryCta: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 18px",
    borderRadius: 10,
    border: "1px solid #15c971",
    background: "linear-gradient(180deg, #31d48b, #16b96b)",
    color: "#072113",
    fontWeight: 800,
    fontSize: 14,
    height: 42,
    textDecoration: "none",
    cursor: "pointer",
    minWidth: 210,
    boxShadow: "0 5px 16px rgba(22,185,107,0.24)",
    transition: "transform 0.15s ease, filter 0.2s ease, box-shadow 0.2s ease",
  },
  secondaryLink: { color: "rgba(189,239,213,.9)", textDecoration: "underline", fontSize: 13, marginLeft: 12 },
  appStoreLink: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: 40,
    padding: "0 14px",
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,.18)",
    background: "rgba(255,255,255,.06)",
    color: "#ecfff4",
    textDecoration: "none",
    fontSize: 13,
    fontWeight: 800,
    marginLeft: 10,
  },
  signupMiniCard: {
    background: "rgba(5,12,8,0.52)",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,.12)",
    padding: 13,
    marginTop: 14,
    display: "grid",
    gap: 10,
  },
  trustRow: {
    marginTop: 10,
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  trustChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    border: "1px solid rgba(255,255,255,.16)",
    borderRadius: 999,
    padding: "5px 10px",
    fontSize: 12,
    color: "#d2f3e3",
    background: "rgba(255,255,255,.035)",
  },
  lockedCard: {
    border: "1px solid rgba(251,191,36,0.35)",
    background: "linear-gradient(150deg, rgba(31,25,12,.82), rgba(17,15,9,.9))",
    borderRadius: 12,
    padding: 16,
    marginTop: 10,
    boxShadow: "0 8px 22px rgba(0,0,0,.16)",
  },
  transparencyWrap: {
    border: "1px solid rgba(255,255,255,.12)",
    borderRadius: 12,
    background: "rgba(0,0,0,.22)",
    marginBottom: 16,
  },
  summaryBtn: {
    width: "100%",
    textAlign: "left",
    background: "transparent",
    border: 0,
    color: "#dff6ea",
    padding: "10px 12px",
    cursor: "pointer",
    fontWeight: 700,
  },
  headerRow: {
    position: "sticky",
    top: 0,
    zIndex: 5,
    background: "rgba(11,24,17,0.94)",
    backdropFilter: "blur(4px)",
    display: "flex",
    gap: 10,
    alignItems: "center",
    marginBottom: 14,
    flexWrap: "wrap",
    padding: "10px 10px 8px",
    border: "1px solid rgba(255,255,255,.08)",
    borderRadius: 12,
    color: "#fff",
  },
  select: { padding: "0 12px", height: 40, borderRadius: 10, border: "1px solid rgba(255,255,255,.18)", background: "rgba(255,255,255,.98)" },
  input: { padding: "0 12px", height: 40, borderRadius: 10, border: "1px solid rgba(255,255,255,.18)", background: "rgba(255,255,255,.98)" },
  heroInput: {
    padding: "0 12px",
    height: 42,
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,.2)",
    background: "rgba(9,18,13,.9)",
    color: "#eefcf5",
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
  },
  btn: {
    padding: "0 14px",
    height: 40,
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,.2)",
    background: "rgba(255,255,255,.95)",
    fontWeight: 600,
    cursor: "pointer",
    transition: "background 0.15s ease, transform 0.1s ease, box-shadow 0.15s ease",
  },
  picksWrap: {
    background: "linear-gradient(175deg, rgba(16,31,22,0.96), rgba(12,23,17,0.96))",
    color: "#fff",
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,.08)",
    padding: 18,
    marginBottom: 20,
    boxShadow: "0 10px 24px rgba(0,0,0,.18)",
  },
  picksTitle: { fontWeight: 760, letterSpacing: "-0.01em", fontSize: 19, marginBottom: 8 },
  pickRow: {
    background: "rgba(255,255,255,.03)",
    borderRadius: 12,
    border: "1px solid rgba(255,255,255,.08)",
    padding: "13px 16px",
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
    gap: 8,
    margin: "8px 0",
    transition: "transform 0.15s ease, box-shadow 0.15s ease",
  },
  pickTeam: { display: "flex", alignItems: "center", gap: 8, fontWeight: 600 },
  pickSub: { fontSize: 12, opacity: 0.86, marginTop: 3, lineHeight: 1.4 },
  sectionTitle: { fontSize: 19, fontWeight: 760, letterSpacing: "-0.01em", marginTop: 22, marginBottom: 8 },

  tbl: { width: "100%", borderCollapse: "collapse" },
  th: {
    textAlign: "left",
    padding: "10px 8px",
    borderBottom: "1px solid rgba(255,255,255,.12)",
    fontSize: 14,
    color: "#eaf4ed",
    background: "rgba(255,255,255,.04)",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "12px 8px",
    borderBottom: "1px solid rgba(255,255,255,.06)",
    fontSize: 14,
    color: "#eaf4ed",
    verticalAlign: "middle",
    transition: "background 0.15s ease",
  },
  tr: {
    transition: "background 0.15s ease",
    cursor: "pointer",
  },

  muted: { color: "rgba(255,255,255,.6)" },
  pillRow: { display: "flex", gap: 8, overflowX: "auto", paddingBottom: 8 },
  pill: (active) => ({
    padding: "6px 12px",
    borderRadius: 999,
    border: "1px solid " + (active ? "#00b562" : "rgba(255,255,255,.2)"),
    background: active ? "#00b562" : "transparent",
    color: active ? "#fff" : "rgba(255,255,255,.8)",
    whiteSpace: "nowrap",
    cursor: "pointer",
    fontSize: 13,
    transition: "all 0.15s ease",
  }),
  picksActions: { textAlign: "center", marginTop: 8 },

  smallCard: {
    background: "linear-gradient(180deg, rgba(17,30,22,0.95), rgba(14,24,18,0.95))",
    borderRadius: 14,
    padding: 16,
    marginBottom: 16,
    border: "1px solid rgba(255,255,255,.08)",
    boxShadow: "0 8px 20px rgba(0,0,0,.14)",
  },

  filterRow: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    flexWrap: "wrap",
    marginTop: 8,
  },
  tiny: { fontSize: 12, opacity: 0.7 },
};

/* -------------------- ACCA block ------------------- */
function AccaBlock({ day }) {
  const { user } = useAuth();
  const [accas, setAccas] = useState([]);
  const [trackingAccaId, setTrackingAccaId] = useState(null);
  const [trackedAccaIds, setTrackedAccaIds] = useState([]);

  useEffect(() => {
    api
      .get("api/public/accas/daily", { params: { day } })
      .then(({ data }) => setAccas(data.accas || []))
      .catch(() => setAccas([]));
  }, [day]);

  const handleTrackAcca = async (t) => {
    if (!user) {
      window.location.href = "/login";
      return;
    }
    try {
      setTrackingAccaId(t.id);
      const stake = Number(t.stake_units ?? 1);

      await placeAndTrackEdge(
        {
          fixture_id: null,
          market: t.title || "ACCA",
          bookmaker: "ACCA",
          price: Number(t.combined_price),
        },
        {
          stake,
          sourceTipsterId: t.tipster_id || null,
        }
      );

      setTrackedAccaIds((prev) => (prev.includes(t.id) ? prev : [...prev, t.id]));
    } catch (e) {
      console.error("Failed to track acca", e);
      alert("Could not add this ACCA to your bet tracker.");
    } finally {
      setTrackingAccaId(null);
    }
  };

  if (!accas.length) return null;

  return (
    <div
      style={{
        background: "#142a52",
        color: "#fff",
        borderRadius: 18,
        padding: 16,
        margin: "16px 0",
      }}
    >
      <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 8 }}>
        Featured ACCAs
      </div>
      {accas.map((t) => (
        <div
          key={t.id}
          style={{
            background: "rgba(255,255,255,.06)",
            borderRadius: 12,
            padding: 12,
            marginBottom: 10,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <div style={{ fontWeight: 700 }}>
              {t.title || "ACCA"} • {t.legs?.length || 0} legs
            </div>
            <div style={{ fontSize: 14 }}>
              <b>Stake:</b> {t.stake_units}u • <b>Combined:</b>{" "}
              {t.combined_price?.toFixed(2)}
            </div>
          </div>

          <div style={{ marginTop: 8 }}>
            {(t.legs || []).map((l, i) => (
              <div
                key={i}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto auto",
                  gap: 8,
                  padding: "6px 0",
                  borderBottom: "1px solid rgba(255,255,255,.08)",
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{l.matchup}</div>
                  <div style={{ fontSize: 12, opacity: 0.85 }}>
                    {prettyComp(l.comp)} • {toUK(l.kickoff_utc, { withZone: true })}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>{formatMarketName(l.market)}</div>
                <div style={{ textAlign: "right" }}>
                  {l.price?.toFixed(2)} {l.bookmaker ? `(${l.bookmaker})` : ""}
                </div>
              </div>
            ))}
          </div>

          {t.note ? (
            <div style={{ fontSize: 12, opacity: 0.9, marginTop: 6 }}>• {t.note}</div>
          ) : null}

          <div style={{ marginTop: 8, textAlign: "right" }}>
            {user ? (
              <button
                style={{ ...S.btn, fontSize: 12, padding: "6px 10px" }}
                onClick={() => handleTrackAcca(t)}
                disabled={trackingAccaId === t.id}
              >
                {trackingAccaId === t.id
                  ? "Tracking…"
                  : trackedAccaIds.includes(t.id)
                  ? "Tracked ✓"
                  : "Track this ACCA"}
              </button>
            ) : (
              <Link
                to="/login"
                style={{ fontSize: 12, color: "#9be7ff", textDecoration: "none" }}
              >
                Log in to track →
              </Link>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/* --------------------- main page ------------------- */
export default function PublicDashboard() {
  const { user, isPremium } = useAuth();
  const { favoriteSports, favoriteTeams, favoriteLeagues } = usePreferences();
  const location = useLocation();
  const navigate = useNavigate();

  const [day, setDay] = useState(todayISO());
  const [sport, setSport] = useState("all");
  const [loading, setLoading] = useState(false);
  const [fixtures, setFixtures] = useState([]);
  const [picks, setPicks] = useState([]);
  const [err, setErr] = useState("");
  const [showAll, setShowAll] = useState(false);

  const [followingFeed, setFollowingFeed] = useState([]);
  const [followingErr, setFollowingErr] = useState("");

  const isMobile = useIsMobile(700);

  const [trackingPickKey, setTrackingPickKey] = useState(null);
  const [trackedPickKeys, setTrackedPickKeys] = useState([]);
  const [featuredCommentOpen, setFeaturedCommentOpen] = useState({});
  const [featuredCommentText, setFeaturedCommentText] = useState({});
  const [featuredCommentsByPick, setFeaturedCommentsByPick] = useState({});
  const [featuredCommentLoadingByPick, setFeaturedCommentLoadingByPick] = useState({});
  const [featuredCommentSavingByPick, setFeaturedCommentSavingByPick] = useState({});
  const [featuredCommentErrByPick, setFeaturedCommentErrByPick] = useState({});

  // NEW: All vs Favourites toggle
  const [showFavsOnly, setShowFavsOnly] = useState(false);

  // NEW: sorting + min edge
  const [sortMode, setSortMode] = useState("league"); // "league" | "kickoff" | "top_edge"
  const [minEdgePct, setMinEdgePct] = useState(0); // percent, e.g. 5 = 5%

  // NEW: top edge cache (per fixture)
  // { [fixtureId]: { edge, market, bookmaker, isTeaser, hasFullAccess } | null }
  const [topEdgeByFixture, setTopEdgeByFixture] = useState({});
  const [topEdgeLoading, setTopEdgeLoading] = useState(false);
  const [topEdgeError, setTopEdgeError] = useState("");
  const [fixtureSocialTags, setFixtureSocialTags] = useState({});
  const [collapsedLeagues, setCollapsedLeagues] = useState(() => {
    try {
      return JSON.parse(window.localStorage.getItem("csb:dashboard:collapsed-leagues:v1") || "{}");
    } catch {
      return {};
    }
  });
  const bulkEdgesAbortRef = useRef(null);
  const bulkEdgesDebounceRef = useRef(null);
  const [signupEmail, setSignupEmail] = useState("");
  const [transparencyOpen, setTransparencyOpen] = useState(false);
  const [fanSpaces, setFanSpaces] = useState([]);
  const [fanSpacesLoading, setFanSpacesLoading] = useState(false);

  const source = useMemo(() => {
    const params = new URLSearchParams(location.search || "");
    return (params.get("src") || params.get("utm_source") || "direct").toLowerCase();
  }, [location.search]);
  const isTikTokVariant = source === "tiktok";
  const ctaLabel = "Start Free — No Card Required";

  const trackEvent = useCallback((event, props = {}) => {
    const payload = { source, ...props };
    if (typeof window !== "undefined") {
      window.dataLayer?.push({ event, ...payload });
      if (typeof window.gtag === "function") {
        window.gtag("event", event, payload);
      }
    }
  }, [source]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = localStorage.getItem("csb_signup_email") || "";
    if (stored && !signupEmail) setSignupEmail(stored);
  }, [signupEmail]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const [{ data: fxJ }, { data: pkJ }] = await Promise.all([
        api.get("/api/public/fixtures/daily", { params: { day, sport } }),
        api.get("/api/public/picks/daily", {
          // These are manually curated and published by an admin. Do not run
          // the automated shortlist health filter over them a second time.
          params: { day, sport, limit: 50, apply_market_trim: 0 },
        }),
      ]);

      setFixtures(fxJ?.fixtures || []);
      setPicks(
        (pkJ?.picks || [])
          .filter((pick) => isAllowedPublicEdge(pick?.edge))
          .sort((a, b) => new Date(a.kickoff_utc || 0) - new Date(b.kickoff_utc || 0))
      );
    } catch (e) {
      setErr(String(e?.response?.data?.detail || e.message || e));
      setFixtures([]);
      setPicks([]);
    } finally {
      setLoading(false);
    }
  }, [day, sport]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setFanSpaces([]);
      setFanSpacesLoading(false);
      return undefined;
    }
    setFanSpacesLoading(true);
    fetchMyTeamRooms({ limit: 8 })
      .then((data) => {
        if (!cancelled) setFanSpaces(data?.rooms || data?.items || []);
      })
      .catch(() => {
        if (!cancelled) setFanSpaces([]);
      })
      .finally(() => {
        if (!cancelled) setFanSpacesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("csb_landing_source", source);
    }
    trackEvent("landing_view");
  }, [source, trackEvent]);

  useEffect(() => {
    let fired = false;
    const onScroll = () => {
      if (fired) return;
      const doc = document.documentElement;
      const maxScrollable = Math.max(1, doc.scrollHeight - window.innerHeight);
      const depth = window.scrollY / maxScrollable;
      if (depth >= 0.5) {
        fired = true;
        trackEvent("hero_scroll_depth_50");
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [trackEvent]);

  // Following mini feed
  useEffect(() => {
    if (!user) {
      setFollowingFeed([]);
      setFollowingErr("");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/api/tipsters/following/feed", {
          params: { limit: 5 },
        });
        if (!cancelled) {
          const rows = Array.isArray(data) ? data : data.picks || [];
          setFollowingFeed(rows);
          setFollowingErr("");
        }
      } catch (e) {
        if (e?.response?.status === 401) {
          if (!cancelled) {
            setFollowingFeed([]);
            setFollowingErr("");
          }
        } else if (!cancelled) {
          setFollowingErr("Could not load your following feed.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // helper: does this fixture match user's favourites?
  const isFavFixture = useCallback(
    (f) => {
      const norm = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
      const sportKey = norm(f.sport);
      const favSportsSet = new Set((favoriteSports || []).map(norm));
      const favLeaguesSet = new Set((favoriteLeagues || []).map(norm));
      const favTeamsSet = new Set((favoriteTeams || []).map(norm));

      if (favSportsSet.has(sportKey)) return true;
      if (favLeaguesSet.has(norm(f.comp)) || favLeaguesSet.has(norm(f.provider_league_id))) return true;
      if (favTeamsSet.has(norm(f.home_team)) || favTeamsSet.has(norm(f.away_team))) return true;
      return false;
    },
    [favoriteSports, favoriteTeams, favoriteLeagues]
  );

  // favourites-first base ordering + rugby league filtering
  const fixturesSorted = useMemo(() => {
    if (!fixtures.length) return [];

    // Filter rugby to only show premium leagues
    const filtered = fixtures.filter((f) => {
      const sportKey = (f.sport || "").toLowerCase();
      if (sportKey === "rugby" || sportKey === "rugby union") {
        const comp = (f.comp || "").toUpperCase();
        return PREMIUM_RUGBY_LEAGUES.some((league) => comp.includes(league));
      }
      return true; // show all other sports
    });

    return [...filtered].sort((a, b) => {
      const aFav = isFavFixture(a);
      const bFav = isFavFixture(b);
      if (aFav !== bFav) return aFav ? -1 : 1;
      return (a.kickoff_utc || "").localeCompare(b.kickoff_utc || "");
    });
  }, [fixtures, isFavFixture]);

  const fixturesToShowBase = useMemo(() => {
    if (!showFavsOnly) return fixturesSorted;
    return fixturesSorted.filter((f) => isFavFixture(f));
  }, [fixturesSorted, showFavsOnly, isFavFixture]);

  // ---------------- Top edge selection (bulk summary endpoint) ----------------
  const pickDisplayTopEdgeFromSummary = useCallback((summary) => {
    if (!summary || typeof summary !== "object") return null;
    if (summary.market == null && summary.bookmaker == null && summary.edge == null) return null;
    if (isWilliamHill(summary.bookmaker)) return null;
    if (!isAllowedPublicEdge(summary.edge)) return null;

    const normalizedEdgePercent = normalizeEdgePercent(summary.edge);
    return {
      market: summary.market || null,
      bookmaker: summary.bookmaker || null,
      edge: normalizedEdgePercent == null ? null : normalizedEdgePercent / 100,
      prob: typeof Number(summary.prob) === "number" && Number.isFinite(Number(summary.prob)) ? Number(summary.prob) : null,
      price: typeof Number(summary.price) === "number" && Number.isFinite(Number(summary.price)) ? Number(summary.price) : null,
      isTeaser: false,
      hasFullAccess: true,
    };
  }, []);

  // load top edges in one bulk request
  useEffect(() => {
    const ids = fixturesToShowBase.map((f) => Number(f.id)).filter(Boolean);
    if (!ids.length) {
      setTopEdgeLoading(false);
      return undefined;
    }

    if (bulkEdgesDebounceRef.current) clearTimeout(bulkEdgesDebounceRef.current);
    if (bulkEdgesAbortRef.current) bulkEdgesAbortRef.current.abort();
    const controller = new AbortController();
    bulkEdgesAbortRef.current = controller;

    bulkEdgesDebounceRef.current = setTimeout(async () => {
      setTopEdgeLoading(true);
      setTopEdgeError("");
      try {
        const fixtureIdsParam = ids.join(",");
        console.log("[PublicDashboard][EdgesSummary] request", {
          endpoint: "/fixtures/edges/summary",
          fixture_ids_count: ids.length,
        });
        const { data: summaryRes } = await api.get("/fixtures/edges/summary", {
          params: { fixture_ids: fixtureIdsParam },
          signal: controller.signal,
        });
        const byFixture = summaryRes?.by_fixture || {};
        const mapped = {};
        const fallbackIds = [];
        ids.forEach((fid) => {
          const summary = byFixture[String(fid)];
          const picked = pickDisplayTopEdgeFromSummary(summary);
          const saved = summary?.saved_edge;
          mapped[fid] = picked || (saved && isAllowedPublicEdge(saved.edge) ? {
            savedEdge: {
              market: saved.market,
              bookmaker: saved.bookmaker,
              edge: Number(saved.edge),
              savedAt: saved.saved_at,
            },
          } : null);
          if (!picked && !saved) fallbackIds.push(fid);
        });

        if (fallbackIds.length) {
          const fallbackResults = await Promise.allSettled(
            fallbackIds.map(async (fid) => {
              const detail = await fetchFixtureEdges(fid);
              const rows = detail?.edges || detail?.edges_teaser || [];
              return [fid, pickBestAllowedPublicEdge(rows)];
            })
          );

          fallbackResults.forEach((result) => {
            if (result.status !== "fulfilled") return;
            const [fid, picked] = result.value;
            if (picked) mapped[fid] = picked;
          });
        }
        if (!controller.signal.aborted) {
          setTopEdgeByFixture(mapped); // full replacement from fresh payload
          console.log("[PublicDashboard][EdgesSummary] response", {
            count: summaryRes?.count ?? 0,
            mapped_count: Object.keys(mapped).length,
          });
        }
      } catch (e) {
        if (controller.signal.aborted) return;
        console.error("[PublicDashboard][EdgesSummary] failed", e);
        const fallback = {};
        ids.forEach((fid) => {
          fallback[fid] = null;
        });
        setTopEdgeByFixture(fallback);
        setTopEdgeError("Could not refresh edge summaries.");
      } finally {
        if (!controller.signal.aborted) setTopEdgeLoading(false);
      }
    }, 150);

    return () => {
      if (bulkEdgesDebounceRef.current) clearTimeout(bulkEdgesDebounceRef.current);
      controller.abort();
    };
  }, [fixturesToShowBase, pickDisplayTopEdgeFromSummary]);

  // Apply min edge threshold to fixtures (based on displayed Top Edge)
  const minEdge = Number(minEdgePct || 0) / 100;

  const fixturesFiltered = useMemo(() => {
    if (!fixturesToShowBase.length) return [];
    if (!minEdge) return fixturesToShowBase;

    return fixturesToShowBase.filter((f) => {
      const te = topEdgeByFixture[Number(f.id)];
      if (!te || typeof te.edge !== "number") return false; // if we don't know yet, hide under filter
      return te.edge >= minEdge;
    });
  }, [fixturesToShowBase, topEdgeByFixture, minEdge]);

  // Sort fixtures by league, kickoff or top edge
  const fixturesFinal = useMemo(() => {
    const arr = [...fixturesFiltered];

    if (sortMode === "league") {
      arr.sort((a, b) => {
        const leagueOrder = prettyComp(a.comp).localeCompare(prettyComp(b.comp), undefined, {
          sensitivity: "base",
        });
        if (leagueOrder !== 0) return leagueOrder;

        const kickoffOrder = (a.kickoff_utc || "").localeCompare(b.kickoff_utc || "");
        if (kickoffOrder !== 0) return kickoffOrder;

        return `${a.home_team || ""} ${a.away_team || ""}`.localeCompare(
          `${b.home_team || ""} ${b.away_team || ""}`,
          undefined,
          { sensitivity: "base" }
        );
      });
      return arr;
    }

    if (sortMode === "top_edge") {
      arr.sort((a, b) => {
        const ae = topEdgeByFixture[Number(a.id)];
        const be = topEdgeByFixture[Number(b.id)];
        const av = typeof ae?.edge === "number" ? ae.edge : -999;
        const bv = typeof be?.edge === "number" ? be.edge : -999;
        if (bv !== av) return bv - av; // higher edge first
        return (a.kickoff_utc || "").localeCompare(b.kickoff_utc || "");
      });
      return arr;
    }

    // default kickoff (already favourites-first from base)
    arr.sort((a, b) => (a.kickoff_utc || "").localeCompare(b.kickoff_utc || ""));
    // re-apply favourites-first stable
    arr.sort((a, b) => {
      const aFav = isFavFixture(a);
      const bFav = isFavFixture(b);
      if (aFav !== bFav) return aFav ? -1 : 1;
      return 0;
    });
    return arr;
  }, [fixturesFiltered, sortMode, topEdgeByFixture, isFavFixture]);

  const fixturesVisible = useMemo(() => {
    if (user) return fixturesFinal;
    return fixturesFinal.slice(0, 8);
  }, [user, fixturesFinal]);

  const fixtureLeagueGroups = useMemo(() => {
    const groups = new Map();
    fixturesVisible.forEach((fixture) => {
      const key = String(fixture.comp || fixture.league || "OTHER");
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(fixture);
    });
    return Array.from(groups.entries())
      .map(([key, rows]) => ({
        key,
        label: prettyComp(key),
        sport: rows[0]?.sport || "",
        rows: [...rows].sort((a, b) => String(a.kickoff_utc || "").localeCompare(String(b.kickoff_utc || ""))),
      }))
      .sort((a, b) => {
        const aFav = a.rows.some(isFavFixture);
        const bFav = b.rows.some(isFavFixture);
        if (aFav !== bFav) return aFav ? -1 : 1;
        return a.label.localeCompare(b.label);
      });
  }, [fixturesVisible, isFavFixture]);

  const toggleLeague = (key) => {
    setCollapsedLeagues((current) => ({ ...current, [key]: !(current[key] ?? true) }));
  };

  const openLeague = (key) => {
    setCollapsedLeagues((current) => ({ ...current, [key]: false }));
    window.requestAnimationFrame(() => {
      document.getElementById(`fixture-league-${slug(key)}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  useEffect(() => {
    try {
      window.localStorage.setItem("csb:dashboard:collapsed-leagues:v1", JSON.stringify(collapsedLeagues));
    } catch {
      // Browsers with storage disabled still get working in-session collapse state.
    }
  }, [collapsedLeagues]);

  useEffect(() => {
    const ids = fixturesVisible.map((f) => f.id).filter(Boolean).slice(0, 60);
    if (!ids.length) {
      setFixtureSocialTags({});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchFixtureSocialPostTags(ids);
        const byFixture = data?.by_fixture || data?.fixtures || data || {};
        if (!cancelled) setFixtureSocialTags(byFixture && typeof byFixture === "object" ? byFixture : {});
      } catch {
        if (!cancelled) setFixtureSocialTags({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fixturesVisible]);

  const fixturesById = useMemo(() => {
    const m = {};
    for (const f of fixtures) m[Number(f.id)] = f;
    return m;
  }, [fixtures]);

  const splitMatchup = (s = "") => {
    const m = s.split(/\s+v(?:s\.)?\s+/i);
    return m.length === 2 ? { home: m[0].trim(), away: m[1].trim() } : {};
  };

  const resolvePick = (p) => {
    const fx = fixturesById[Number(p.fixture_id ?? p.id)];
    if (fx)
      return {
        fixture_id: fx.id,
        home: fx.home_team,
        away: fx.away_team,
        comp: fx.comp,
        ko: fx.kickoff_utc,
        sport: fx.sport || p.sport,
      };
    const parsed = splitMatchup(p.matchup || "");
    return {
      fixture_id: p.fixture_id ?? p.id,
      home: p.home_team || parsed.home || "—",
      away: p.away_team || parsed.away || "—",
      comp: p.comp || "Football",
      ko: p.kickoff_utc,
      sport: p.sport,
    };
  };

  const sportOptions = ["all", "football", "baseball", "nba", "nhl", "nfl", "cfb", "rugby"];
  const shown = showAll ? picks : picks.slice(0, 3);
  const loggedOutFreePicks = useMemo(
    () => picks.filter((p) => !p.is_premium_only).slice(0, 2),
    [picks]
  );
  const lockedTeaserPick = useMemo(
    () => picks.find((p) => p.is_premium_only) || picks[2] || null,
    [picks]
  );
  const shownPicks = !user ? loggedOutFreePicks : shown;

  const startSignup = useCallback(() => {
    trackEvent("cta_click_start_free");
    navigate(`/signup${location.search || ""}`);
  }, [trackEvent, navigate, location.search]);

  const submitSignupStep1 = useCallback(
    (evt) => {
      evt.preventDefault();
      const email = signupEmail.trim();
      if (!email) return;
      if (typeof window !== "undefined") {
        localStorage.setItem("csb_signup_email", email);
      }
      trackEvent("signup_step1_email_submitted");
      const params = new URLSearchParams(location.search || "");
      params.set("email", email);
      if (!params.get("src") && source && source !== "direct") params.set("src", source);
      navigate(`/signup?${params.toString()}`);
    },
    [signupEmail, trackEvent, location.search, source, navigate]
  );

  const handleTrackPick = async (p, resolvedPick, pickKey, evt) => {
    if (evt) {
      evt.preventDefault();
      evt.stopPropagation();
    }

    if (!user) {
      window.location.href = "/login";
      return;
    }

    const bmUrl = getBookmakerUrl(p.bookmaker);
    const openUrl = bmUrl
      ? bmUrl
      : "https://google.com/search?q=" + encodeURIComponent(p.bookmaker || "bet365");

    window.open(openUrl, "_blank", "noopener");

    try {
      setTrackingPickKey(pickKey);
      const stake = Number(p.stake ?? 1);

      await placeAndTrackEdge(
        {
          fixture_id: Number(resolvedPick.fixture_id) || null,
          market: p.market,
          bookmaker: p.bookmaker || null,
          price: Number(p.price),
        },
        { stake, sourceTipsterId: p.tipster_id || null }
      );

      setTrackedPickKeys((prev) => (prev.includes(pickKey) ? prev : [...prev, pickKey]));
    } catch (e) {
      console.error("Failed to track featured pick", e);
      alert("Could not add this pick to your bet tracker.");
    } finally {
      setTrackingPickKey(null);
    }
  };

  const getFeaturedCommentTargetId = (pick) => pick?.id ?? pick?.pick_id ?? null;

  const loadFeaturedComments = async (pick) => {
    const targetId = getFeaturedCommentTargetId(pick);
    if (!targetId) return;
    setFeaturedCommentLoadingByPick((prev) => ({ ...prev, [targetId]: true }));
    setFeaturedCommentErrByPick((prev) => ({ ...prev, [targetId]: "" }));
    try {
      const data = await fetchSocialComments({
        target_type: "featured_pick",
        target_id: targetId,
        limit: 100,
      });
      const rows = Array.isArray(data) ? data : data?.comments || [];
      setFeaturedCommentsByPick((prev) => ({ ...prev, [targetId]: rows }));
    } catch (e) {
      setFeaturedCommentErrByPick((prev) => ({ ...prev, [targetId]: "Comments unavailable." }));
    } finally {
      setFeaturedCommentLoadingByPick((prev) => ({ ...prev, [targetId]: false }));
    }
  };

  const toggleFeaturedComments = async (pick, evt) => {
    if (evt) {
      evt.preventDefault();
      evt.stopPropagation();
    }
    const targetId = getFeaturedCommentTargetId(pick);
    if (!targetId) return;
    const nextOpen = !featuredCommentOpen[targetId];
    setFeaturedCommentOpen((prev) => ({ ...prev, [targetId]: nextOpen }));
    if (nextOpen && !featuredCommentsByPick[targetId]) {
      await loadFeaturedComments(pick);
    }
  };

  const submitFeaturedComment = async (pick, evt) => {
    if (evt) {
      evt.preventDefault();
      evt.stopPropagation();
    }
    const targetId = getFeaturedCommentTargetId(pick);
    if (!targetId) return;
    if (!user) {
      window.location.href = "/login";
      return;
    }
    const body = (featuredCommentText[targetId] || "").trim();
    if (!body) return;

    setFeaturedCommentSavingByPick((prev) => ({ ...prev, [targetId]: true }));
    setFeaturedCommentErrByPick((prev) => ({ ...prev, [targetId]: "" }));
    try {
      const created = await createSocialComment({
        target_type: "featured_pick",
        target_id: targetId,
        body,
      });
      setFeaturedCommentsByPick((prev) => ({
        ...prev,
        [targetId]: [created, ...(prev[targetId] || [])],
      }));
      setFeaturedCommentText((prev) => ({ ...prev, [targetId]: "" }));
    } catch (e) {
      setFeaturedCommentErrByPick((prev) => ({ ...prev, [targetId]: "Could not post comment." }));
    } finally {
      setFeaturedCommentSavingByPick((prev) => ({ ...prev, [targetId]: false }));
    }
  };

  return (
    <div style={S.page}>
      {!user && (
        <>
          <section style={S.heroWrap}>
            {isTikTokVariant && (
              <div style={S.socialStrip}>
                Built for football bettors • Real-time markets • Free to start
              </div>
            )}
            <h1 style={S.heroTitle}>
              Get today&apos;s best model-rated picks, stats, and tracking in under 60 seconds.
            </h1>
            <p style={S.heroSub}>
              Free account unlocks today&apos;s card across sports, plus league/team/player stats, performance tracking, and tipster follows. No card required.
            </p>
            {isTikTokVariant && (
              <div style={S.heroBullets}>
                <span>• Today&apos;s model edges</span>
                <span>• Track picks + performance</span>
                <span>• Follow top tipsters</span>
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", marginTop: 14 }}>
              <button type="button" style={S.primaryCta} onClick={startSignup}>
                {ctaLabel}
              </button>
              <Link to="/premium" style={S.secondaryLink}>
                See Premium
              </Link>
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noreferrer"
                style={S.appStoreLink}
              >
                Get the iOS app
              </a>
            </div>
            <form style={S.signupMiniCard} onSubmit={submitSignupStep1}>
              <div style={{ fontWeight: 700, color: "#ecfff4" }}>Start free in two steps</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", flexDirection: isMobile ? "column" : "row" }}>
                <input
                  type="email"
                  value={signupEmail}
                  onChange={(e) => setSignupEmail(e.target.value)}
                  placeholder="Email address"
                  style={{
                    ...S.heroInput,
                    borderRadius: isMobile ? 10 : "10px 0 0 10px",
                    minWidth: isMobile ? "100%" : 230,
                    flex: 1,
                  }}
                  required
                />
                <button
                  type="submit"
                  style={{
                    ...S.primaryCta,
                    borderRadius: isMobile ? 10 : "0 10px 10px 0",
                    width: isMobile ? "100%" : undefined,
                    minWidth: isMobile ? "100%" : 148,
                  }}
                >
                  Continue
                </button>
              </div>
            </form>
            <div style={S.trustRow}>
              <span style={S.trustChip}>✓ No card required</span>
              <span style={S.trustChip}>⚡ Multi-sport edges + stats</span>
              <span style={S.trustChip}>↗ Track picks &amp; performance</span>
            </div>
          </section>
        </>
      )}

      {/* Header controls */}
      <div style={S.headerRow}>
        <div style={{ fontWeight: 800, fontSize: 22 }}>Today’s Card</div>
        {!user && (
          <div style={{ fontSize: 12, color: "rgba(255,255,255,.78)" }}>
            Includes picks, live odds context, and deep stats across football, basketball, hockey, and more.
          </div>
        )}
        <div style={{ marginLeft: "auto" }} />

        <label>
          Day:&nbsp;
          <input
            style={S.input}
            type="date"
            value={day}
            onChange={(e) => {
              setDay(e.target.value);
              setTopEdgeByFixture({}); // reset cache when day changes
            }}
          />
        </label>

        {!isMobile && (
          <label>
            &nbsp;Sport:&nbsp;
            <select
              style={S.select}
              value={sport}
              onChange={(e) => {
                setSport(e.target.value);
                setTopEdgeByFixture({});
              }}
            >
              {sportOptions.map((s) => (
                <option key={s} value={s}>
                  {s.toUpperCase()}
                </option>
              ))}
            </select>
          </label>
        )}

        <button style={S.btn} onClick={load}>
          Refresh
        </button>

        {isMobile && (
          <div style={{ width: "100%" }}>
            <div style={S.pillRow}>
              {sportOptions.map((s) => (
                <button
                  key={s}
                  onClick={() => {
                    setSport(s);
                    setTopEdgeByFixture({});
                  }}
                  style={S.pill(sport === s)}
                >
                  {s.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ⭐ Premium Upsell Banner */}
      {!!user && (
        <PremiumUpsellBanner
          mode="link"
          message="Go Premium to unlock full CSB model edges, extra featured picks, and deeper stats across today’s card."
        />
      )}

      <Link className={dashboardStyles.dailyPlayerPromo} to="/daily-player">
        <span className={dashboardStyles.dailyPlayerBall}>?</span>
        <span><small>CSB DAILY CHALLENGE</small><strong>Guess the Player</strong><em>Six guesses. One footballer. Build your streak.</em></span>
        <b>Play today →</b>
      </Link>

      <section className={dashboardStyles.fanSpaces}>
        <div className={dashboardStyles.fanSpacesHeader}>
          <div>
            <div className={dashboardStyles.fanSpacesKicker}>Your fan communities</div>
            <h2>{fanSpaces.length ? "Jump back into your fan spaces" : "Find your fan space"}</h2>
            <p>Club conversation, matchday reactions and the fans who make sport matter.</p>
          </div>
          <Link className={dashboardStyles.fanSpacesDiscover} to="/communities">
            {fanSpaces.length ? "Discover more" : "Explore communities"} <span aria-hidden="true">→</span>
          </Link>
        </div>

        {fanSpacesLoading ? (
          <div className={dashboardStyles.fanSpacesLoading}>Loading your communities…</div>
        ) : fanSpaces.length ? (
          <div className={dashboardStyles.fanSpacesRail}>
            {fanSpaces.slice(0, 6).map((room) => {
              const name = room.team_name || room.name || "Club";
              const teamId = room.team_id || room.csb_team_id || room.id;
              const logo = room.team_logo || room.logo || room.crest;
              const roomCopy = getClubRoomCopy(room.sport);
              const destination = teamId
                ? `/club/${teamId}/${slugifyTeamName(name)}?tab=community`
                : `/search?q=${encodeURIComponent(name)}&tab=clubs`;
              return (
                <Link className={dashboardStyles.fanSpaceCard} to={destination} key={`${teamId || name}-${room.sport || ""}`}>
                  <span className={dashboardStyles.fanSpaceLogo}>
                    {logo ? <img src={logo} alt="" loading="lazy" /> : name.charAt(0).toUpperCase()}
                  </span>
                  <span className={dashboardStyles.fanSpaceCopy}>
                    <strong>{name}</strong>
                    <span>{prettyCompetitionName(room.league_name || room.league_key || room.sport || "Fan community", room.sport)} · {roomCopy.room}</span>
                  </span>
                  <span className={dashboardStyles.fanSpaceArrow} aria-hidden="true">↗</span>
                </Link>
              );
            })}
          </div>
        ) : (
          <Link className={dashboardStyles.fanSpacesEmpty} to="/communities">
            <span className={dashboardStyles.fanSpacesEmptyIcon}>◉</span>
            <span><strong>Join the conversation</strong><small>Choose your clubs and keep their fan spaces one click away.</small></span>
            <span aria-hidden="true">↗</span>
          </Link>
        )}
      </section>

      <SportsNewsSection
        mode="talking-points"
        day={day}
        title="Talking Points"
        limit={6}
        emptyMessage="No major talking points yet."
        dark
      />

      {/* Featured Picks */}
      <div style={S.picksWrap}>
        {!user && (
          <div style={{ marginBottom: 10, fontSize: 13, color: "#d8f6e6" }}>
            Create a free account to unlock today&apos;s full card, tracking, and tipster follows.
          </div>
        )}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <div style={S.picksTitle}>Featured Picks</div>
          {user && <FeaturedRecord />}
        </div>

        {picks.length === 0 ? (
          <div
            style={{
              background: "rgba(255,255,255,.08)",
              borderRadius: 12,
              padding: 12,
              color: "#d7e6db",
            }}
          >
            No featured picks yet.
          </div>
        ) : (
          <>
            {shownPicks.map((p, i) => {
              const d = resolvePick(p);
              const pickMeta = featuredPickMeta(p);
              const metaSegments = [
                p.price != null ? `@ ${Number(p.price).toFixed(2)}` : null,
                p.bookmaker,
                pickMeta.edgeText,
                pickMeta.isPlayerProp && pickMeta.fairText ? `Fair ${pickMeta.fairText}` : null,
                pickMeta.isPlayerProp && pickMeta.modelText ? `Model ${pickMeta.modelText}` : null,
              ].filter(Boolean);
              const pickKey = `${day}_${sport}_${i}`;
              const tracked = trackedPickKeys.includes(pickKey);
              const tracking = trackingPickKey === pickKey;
              const commentTargetId = getFeaturedCommentTargetId(p);
              const commentRows = commentTargetId ? (featuredCommentsByPick[commentTargetId] || []) : [];
              const commentOpen = commentTargetId ? !!featuredCommentOpen[commentTargetId] : false;
              const commentLoading = commentTargetId ? !!featuredCommentLoadingByPick[commentTargetId] : false;
              const commentSaving = commentTargetId ? !!featuredCommentSavingByPick[commentTargetId] : false;
              const commentErr = commentTargetId ? featuredCommentErrByPick[commentTargetId] : "";
              const commentText = commentTargetId ? (featuredCommentText[commentTargetId] || "") : "";

              return (
                <div key={i}>
                  <Link
                    to={routeFor({ id: d.fixture_id, sport: d.sport })}
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    <div style={S.pickRow}>
                      <div style={S.pickTeam}>
                        <img
                          src={logoUrl(d.home)}
                          width={20}
                          alt=""
                          onError={(e) => (e.currentTarget.style.display = "none")}
                        />{" "}
                        {d.home}
                      </div>

                      <div style={{ textAlign: "center" }}>
                        <div>{featuredKickoffLabel(d.ko, day)}</div>
                        <div style={{ fontSize: 11, opacity: 0.75 }}>
                          {prettyComp(d.comp)}
                        </div>
                      </div>

                      <div style={{ ...S.pickTeam, justifyContent: "flex-end" }}>
                        {d.away}{" "}
                        <img
                          src={logoUrl(d.away)}
                          width={20}
                          alt=""
                          onError={(e) => (e.currentTarget.style.display = "none")}
                        />
                      </div>

                      <div
                        style={{
                          gridColumn: "1 / -1",
                          ...S.pickSub,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 8,
                        }}
                      >
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          {pickMeta.isPlayerProp && p.player_id ? (
                            <span
                              style={{
                                width: 24,
                                height: 24,
                                borderRadius: 999,
                                overflow: "hidden",
                                background: "rgba(255,255,255,0.08)",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <img
                                src={playerHeadshot(p.player_id)}
                                width={24}
                                height={24}
                                alt=""
                                style={{ objectFit: "cover" }}
                                onError={(e) => (e.currentTarget.style.display = "none")}
                              />
                            </span>
                          ) : null}
                          {featuredPickLabel(p)}
                          {p.is_premium_only && (
                            <span
                              style={{
                                padding: "2px 8px",
                                borderRadius: 999,
                                background: "rgba(255,255,255,0.06)",
                                border: "1px solid rgba(251,191,36,0.7)",
                                color: "#FBBF24",
                                fontSize: 11,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              🔒 Premium
                            </span>
                          )}
                          {metaSegments.length ? ` • ${metaSegments.join(" • ")}` : ""}
                        </span>

                        <div style={{ display: "inline-flex", gap: 8 }}>
                          <button
                            type="button"
                            style={{ ...S.btn, fontSize: 12, padding: "4px 10px" }}
                            onClick={(evt) => toggleFeaturedComments(p, evt)}
                            disabled={!commentTargetId}
                          >
                            💬 Comment {commentRows.length ? `(${commentRows.length})` : ""}
                          </button>
                          {user && (
                            <button
                              type="button"
                              style={{ ...S.btn, fontSize: 12, padding: "4px 10px" }}
                              onClick={(evt) => handleTrackPick(p, d, pickKey, evt)}
                              disabled={tracking}
                            >
                              {tracking ? "Tracking…" : tracked ? "Tracked ✓" : "Track"}
                            </button>
                          )}
                        </div>
                      </div>
                      {pickMeta.isPlayerProp ? (
                        <div
                          style={{
                            gridColumn: "1 / -1",
                            marginTop: 6,
                            fontSize: 12,
                            color: "#d8f6e6",
                            display: "flex",
                            gap: 8,
                            flexWrap: "wrap",
                          }}
                        >
                          {p.line != null ? <span>Line {p.line}</span> : null}
                          {p.note ? <span style={{ opacity: 0.8 }}>{p.note}</span> : null}
                        </div>
                      ) : null}
                    </div>
                  </Link>

                  {commentOpen && commentTargetId && (
                    <div
                      style={{
                        marginTop: 6,
                        marginBottom: 10,
                        padding: 10,
                        borderRadius: 10,
                        background: "rgba(255,255,255,0.06)",
                        border: "1px solid rgba(255,255,255,0.1)",
                      }}
                    >
                      {commentLoading && <div style={{ fontSize: 12, opacity: 0.75 }}>Loading comments…</div>}
                      {commentErr ? (
                        <div style={{ fontSize: 12, color: "#ffb3b3", marginBottom: 8 }}>{commentErr}</div>
                      ) : null}

                      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                        <input
                          value={commentText}
                          onChange={(e) =>
                            setFeaturedCommentText((prev) => ({
                              ...prev,
                              [commentTargetId]: e.target.value,
                            }))
                          }
                          placeholder={user ? "Write a comment…" : "Log in to comment"}
                          disabled={!user}
                          style={{
                            flex: 1,
                            borderRadius: 8,
                            border: "1px solid rgba(255,255,255,0.2)",
                            background: "rgba(0,0,0,0.45)",
                            color: "#fff",
                            fontSize: 12,
                            padding: "7px 9px",
                          }}
                        />
                        <button
                          type="button"
                          style={{ ...S.btn, fontSize: 12, padding: "6px 10px" }}
                          onClick={(evt) => submitFeaturedComment(p, evt)}
                          disabled={!user || commentSaving || !commentText.trim()}
                        >
                          {commentSaving ? "Posting…" : "Post"}
                        </button>
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {commentRows.map((c, ci) => (
                          <div
                            key={c.id || `${c.user_id || "u"}-${ci}`}
                            style={{
                              padding: "7px 8px",
                              borderRadius: 8,
                              background: "rgba(0,0,0,0.38)",
                              border: "1px solid rgba(255,255,255,0.08)",
                            }}
                          >
                            <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 3 }}>
                              {c.display_name || c.username || c.user_name || "User"}
                            </div>
                            <div style={{ fontSize: 12 }}>{c.body || c.comment || ""}</div>
                          </div>
                        ))}
                        {!commentLoading && !commentRows.length && (
                          <div style={{ fontSize: 12, opacity: 0.7 }}>No comments yet.</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {user && picks.length > 3 && (
              <div style={S.picksActions}>
                <button style={S.btn} onClick={() => setShowAll((s) => !s)}>
                  {showAll ? "Show less" : `Show all (${picks.length})`}
                </button>
              </div>
            )}

            {!user && (
              <>
                {lockedTeaserPick && (
                  <div style={S.lockedCard}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                      <div style={{ fontWeight: 800, color: "#fde68a" }}>Premium Teaser</div>
                      <span
                        style={{
                          borderRadius: 999,
                          border: "1px solid rgba(251,191,36,0.7)",
                          color: "#FBBF24",
                          padding: "2px 8px",
                          fontSize: 11,
                        }}
                      >
                        LOCKED
                      </span>
                    </div>
                    <div style={{ marginTop: 8, filter: "blur(3px)", userSelect: "none" }}>
                      {featuredPickLabel(lockedTeaserPick)} • {lockedTeaserPick.bookmaker || "Top book"} •{" "}
                      {typeof lockedTeaserPick.edge === "number"
                        ? `${(lockedTeaserPick.edge * 100).toFixed(1)}% edge`
                        : "High edge"}
                    </div>
                    <div style={{ marginTop: 10 }}>
                      <Link
                        to="/premium"
                        style={{
                          ...S.primaryCta,
                          minWidth: 0,
                          fontSize: 13,
                          padding: "8px 12px",
                          display: "inline-flex",
                        }}
                      >
                        Unlock Premium
                      </Link>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Following mini feed */}
      {user && (
      <div style={S.smallCard}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>From tipsters you follow</div>
          <div style={{ marginLeft: "auto", fontSize: 12 }}>
            <Link to="/following" style={{ color: "#9be7ff", textDecoration: "none" }}>
              View full feed →
            </Link>
          </div>
        </div>

        {!user && (
          <div style={{ fontSize: 13, color: "#d7e6db" }}>
            <Link to="/login" style={{ color: "#9be7ff", textDecoration: "none" }}>
              Log in
            </Link>{" "}
            to see picks from tipsters you follow.
          </div>
        )}

        {user && followingErr && (
          <div style={{ fontSize: 13, color: "#ffb3b3" }}>{followingErr}</div>
        )}

        {user && !followingErr && followingFeed.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ ...S.tbl, marginTop: 4 }}>
              <thead>
                <tr>
                  <th style={S.th}>Tipster</th>
                  <th style={S.th}>Fixture</th>
                  <th style={S.th}>Market</th>
                  <th style={S.th}>Bookmaker</th>
                  <th style={S.th}>Odds</th>
                  <th style={S.th}>Stake</th>
                  <th style={S.th}>Result</th>
                  <th style={{ ...S.th, textAlign: "right" }}>Profit</th>
                </tr>
              </thead>
              <tbody>
                {followingFeed.map((p) => {
                  const locked = p.is_premium_only && !isPremium;

                  return (
                    <tr key={p.id}>
                      <td style={S.td}>
                        <Link
                          to={`/tipsters/${p.tipster_username}`}
                          style={{ color: "#9be7ff", textDecoration: "none" }}
                        >
                          {p.tipster_name || p.tipster_username}
                        </Link>
                      </td>
                      <td style={S.td}>{p.fixture_label}</td>

                      <td style={S.td}>
                        {locked ? (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                            <span style={{ opacity: 0.9 }}>Premium pick</span>
                            <span
                              style={{
                                padding: "2px 6px",
                                borderRadius: 999,
                                background: "rgba(255,255,255,0.06)",
                                border: "1px solid rgba(251,191,36,0.7)",
                                color: "#FBBF24",
                                fontSize: 10,
                              }}
                            >
                              🔒
                            </span>
                            <Link
                              to="/premium"
                              style={{ color: "#FBBF24", textDecoration: "underline", fontSize: 11 }}
                            >
                              Unlock +
                            </Link>
                          </span>
                        ) : (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                            {featuredPickLabel(p)}
                            {p.is_premium_only && (
                              <span
                                style={{
                                  padding: "2px 6px",
                                  borderRadius: 999,
                                  background: "rgba(255,255,255,0.06)",
                                  border: "1px solid rgba(251,191,36,0.7)",
                                  color: "#FBBF24",
                                  fontSize: 10,
                                }}
                              >
                                🔒
                              </span>
                            )}
                          </span>
                        )}
                      </td>

                      <td style={S.td}>{locked ? "—" : p.bookmaker || "—"}</td>
                      <td style={S.td}>{locked ? "—" : p.price?.toFixed?.(2) ?? p.price ?? "—"}</td>
                      <td style={S.td}>{locked ? "—" : p.stake ?? 1}</td>
                      <td style={S.td}>{locked ? "—" : p.result || "—"}</td>

                      <td
                        style={{
                          ...S.td,
                          textAlign: "right",
                          color: locked ? "#eaf4ed" : (p.profit ?? 0) >= 0 ? "#1db954" : "#d23b3b",
                        }}
                      >
                        {locked ? "—" : typeof p.profit === "number" ? p.profit.toFixed(2) : "0.00"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {/* Premium Community Chat */}
      {user && (
      <div style={{ marginBottom: 16 }}>
        <PublicChat variant="compact" maxHeight={350} />
      </div>
      )}

      {/* ACCAs */}
      {user && <AccaBlock day={day} />}

      {/* Fixtures header + toggle + filters */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 18, marginBottom: 8 }}>
        <div style={S.sectionTitle}>{user ? "All Fixtures" : "Today’s Card Preview"}</div>

        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          <button type="button" style={S.pill(!showFavsOnly)} onClick={() => setShowFavsOnly(false)}>
            All
          </button>
          <button type="button" style={S.pill(showFavsOnly)} onClick={() => setShowFavsOnly(true)}>
            Favourites
          </button>
        </div>
      </div>

      {/* Sort + Min edge filters */}
      <div style={{ ...S.smallCard, marginTop: 0 }}>
        <div style={S.filterRow}>
          <label style={S.tiny}>
            Sort:&nbsp;
            <select
              style={{ ...S.select, padding: "6px 10px" }}
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value)}
            >
              <option value="league">League</option>
              <option value="kickoff">Kick-off time</option>
              <option value="top_edge">Top edge</option>
            </select>
          </label>

          <label style={S.tiny}>
            Min edge %:&nbsp;
            <input
              style={{ ...S.input, padding: "6px 10px", width: 90 }}
              type="number"
              min={0}
              step={0.5}
              value={minEdgePct}
              onChange={(e) => setMinEdgePct(e.target.value)}
            />
          </label>

          <div style={{ marginLeft: "auto", fontSize: 12, opacity: 0.85 }}>
            {topEdgeLoading ? "Calculating top edges…" : ""}
            <span style={{ marginLeft: 8, opacity: 0.8 }}>
              (WilliamHill auto-hidden)
            </span>
          </div>
        </div>
        {!!topEdgeError && (
          <div style={{ marginTop: 8, fontSize: 12, color: "#fca5a5" }}>{topEdgeError}</div>
        )}

        <div style={{ marginTop: 8, fontSize: 12, opacity: 0.85 }}>
          Premium users see the true best edge. Free users may see a preview edge until they unlock the fixture or go Premium.
        </div>
      </div>

      {err && <p style={{ color: "#c00" }}>{err}</p>}

      {loading ? (
        <p>Loading…</p>
      ) : !fixtures.length ? (
        <p style={S.muted}>No fixtures found.</p>
      ) : showFavsOnly && !fixturesVisible.length ? (
        <p style={S.muted}>
          No fixtures match your favourites yet. Add favourite teams/leagues on your Account page or from a fixture.
        </p>
      ) : (
        <div className={dashboardStyles.fixturesLayout}>
          <aside className={dashboardStyles.leagueRail} aria-label="Leagues on this card">
            <div className={dashboardStyles.railTitle}>Leagues</div>
            {fixtureLeagueGroups.map((group) => (
              <button className={dashboardStyles.railButton} type="button" key={group.key} onClick={() => openLeague(group.key)}>
                <span className={dashboardStyles.railName}>{group.label}</span>
                <span className={dashboardStyles.railCount}>{group.rows.length}</span>
              </button>
            ))}
          </aside>

          <div className={dashboardStyles.leagueStack}>
            {fixtureLeagueGroups.map((group) => {
              const collapsed = collapsedLeagues[group.key] ?? true;
              return (
                <section className={dashboardStyles.leagueCard} id={`fixture-league-${slug(group.key)}`} key={group.key}>
                  <button className={dashboardStyles.leagueHeader} type="button" onClick={() => toggleLeague(group.key)} aria-expanded={!collapsed}>
                    <span className={dashboardStyles.leagueMeta}>
                      <span className={dashboardStyles.leagueName}>{group.label}</span>
                      <span className={dashboardStyles.sportName}>{group.sport || "Sport"} · {group.rows.length} {group.rows.length === 1 ? "match" : "matches"}</span>
                    </span>
                    <span className={dashboardStyles.collapseIcon}>{collapsed ? "▼" : "▲"}</span>
                  </button>

                  {!collapsed && group.rows.map((fixture) => {
                    const status = getFixtureStatusMeta(fixture);
                    const score = getFixtureScoreMeta(fixture);
                    const edge = topEdgeByFixture[Number(fixture.id)];
                    return (
                      <div className={dashboardStyles.fixtureRow} key={fixture.id}>
                        <div className={`${dashboardStyles.time} ${status.isLive ? dashboardStyles.live : ""}`}>
                          {status.isLive ? "● " : ""}{getFixtureTimeLabel(fixture)}
                        </div>
                        <div>
                          <Link className={dashboardStyles.matchLink} to={routeFor(fixture)}>
                            <span className={dashboardStyles.teamLine}>
                              <span className={dashboardStyles.teamIdentity}>
                                <img className={dashboardStyles.teamLogo} loading="lazy" src={fixture.home_logo || logoUrl(fixture.home_team)} alt="" onError={(event) => { event.currentTarget.style.visibility = "hidden"; }} />
                                <span>{fixture.home_team}</span>
                              </span>
                              {score.hasScore ? <span className={dashboardStyles.score}>{score.home}</span> : null}
                            </span>
                            <span className={dashboardStyles.teamLine}>
                              <span className={dashboardStyles.teamIdentity}>
                                <img className={dashboardStyles.teamLogo} loading="lazy" src={fixture.away_logo || logoUrl(fixture.away_team)} alt="" onError={(event) => { event.currentTarget.style.visibility = "hidden"; }} />
                                <span>{fixture.away_team}</span>
                              </span>
                              {score.hasScore ? <span className={dashboardStyles.score}>{score.away}</span> : null}
                            </span>
                          </Link>
                          <FixtureSocialPreview
                            fixtureId={fixture.id}
                            preview={fixtureSocialTags[String(fixture.id)] || fixtureSocialTags[Number(fixture.id)]}
                            to={routeFor(fixture)}
                            dark
                          />
                        </div>
                        <div className={dashboardStyles.edge}>
                          {edge === undefined ? "Finding top edge…" : edge && typeof edge.edge === "number" ? (
                            <><strong>{(edge.edge * 100).toFixed(1)}%</strong> · {formatMarketName(edge.market, { short: true })}<br />{edge.bookmaker || ""}{edge.isTeaser ? " · Preview" : ""}</>
                          ) : edge?.savedEdge ? (
                            <>
                              <span>No current qualifying edge</span><br />
                              <span className={dashboardStyles.savedEdge}>
                                Saved model edge: {(edge.savedEdge.edge * 100).toFixed(1)}% · {formatMarketName(edge.savedEdge.market, { short: true })}
                                {edge.savedEdge.savedAt ? ` · ${new Date(edge.savedEdge.savedAt).toLocaleString("en-GB", { timeZone: "Europe/London", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}
                                <br />Historical snapshot; odds and model need rechecking.
                              </span>
                            </>
                          ) : "No qualifying edge"}
                        </div>
                        <div className={dashboardStyles.access}><FixtureAccessPill variant="compact" fixtureId={fixture.id} /></div>
                      </div>
                    );
                  })}
                </section>
              );
            })}
          </div>
        </div>
      )}

      {!user && (
        <>
          <section style={S.transparencyWrap}>
            <button
              type="button"
              style={S.summaryBtn}
              onClick={() => setTransparencyOpen((prev) => !prev)}
            >
              Transparency {transparencyOpen ? "▲" : "▼"}
            </button>
            {transparencyOpen && (
              <div style={{ padding: "0 12px 12px" }}>
                <div style={{ fontSize: 13, color: "#cfead8", marginBottom: 8 }}>
                  Full performance metrics live here for context.
                </div>
                <FeaturedRecord />
              </div>
            )}
          </section>

          <PremiumUpsellBanner
            mode="link"
            message="See what premium unlocks: full model edges, deeper market context, and more picks."
          />
        </>
      )}
      <SiteFooterDisclaimer variant="short" />
    </div>
  );
}
